/*!
 * index.js — /zodiac-sign.
 *
 * The tool answers one question, and the whole design follows from a measured
 * fact about that question: for 97.5 % of birth dates the answer needs one
 * field, and for the remaining 2.5 % it CANNOT be given from one field at all.
 *
 *   • every calculator in this segment asks one field and answers all of them
 *     confidently — wrong for 1.3–1.5 % of birthdays, and two widely used date
 *     tables disagree with each other on 2.5 % (measured over 24 837 dates);
 *   • the one respected English site asks all three fields of everybody, and
 *     then still says "your sign is one of two" on a cusp.
 *
 * So the form starts as one field, and the tool ITSELF opens the time and place
 * inputs — with an explanation — when the date it was given is one where the
 * hour decides. The question is asked of the people whose answer depends on it,
 * and of nobody else. That is the root fix; everything below is presentation.
 *
 * Decisions worth knowing:
 *
 * 1. The answer appears as the date completes, not on a button press. The
 *    button stays because a form without one is a puzzle, but it is no longer
 *    the only path.
 *
 * 2. The cusp day is drawn as a BAR of that day split at the crossing moment.
 *    "You are on the cusp and have traits of both" is folklore standing in for
 *    an answer; "the Sun crossed at 16:21, you were born at 09:00" is the
 *    answer, and the bar makes it true at a glance.
 *
 * 3. The eastern sign is computed from the real lunisolar new year, not from
 *    the calendar year. Year-only tables are wrong for everyone born between
 *    1 January and the new moon — about a sixth of each year's births.
 *
 * 4. Nothing here calls astrology a science, and no number is invented. The
 *    element, modality, ruler and decan are positional definitions; the sign,
 *    the degree, the ingress instant, the constellation and the new year are
 *    computed. Character descriptions are labelled as tradition.
 */
import {
    SIGN_KEYS, RULERS_TRADITIONAL, RULERS_MODERN,
    elementOf, modalityOf, polarityOf, oppositeOf, decanOf,
    loadTables, resolveFromTables, resolveWithEngine, signWindow, easternYear, loadEngine,
    siderealFrom,
} from './engine.js';
import { AYANAMSHAS, ayanamsaAt } from '../natal/ayanamsa.js';
import { zoneOffsetAt, localToUtc, formatOffset, dstShiftAt, zoneIsKnown } from '../natal/timeresolve.js';
import { glyphSvg } from '../natal/glyphs.js';
import { ringSvg, dayBarHtml, cardSvg, svgToPngBlob } from './visual.js';

const T = window.__ZD_I18N || {};
const t = (k, fb) => (T[k] !== undefined ? T[k] : (fb !== undefined ? fb : k));
const $ = (id) => document.getElementById(id);
const SLUG = 'zodiac-sign';
const HOUR = 3600000, DAY = 86400000;

const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

let TABLES = null;
let tableRetried = false;
let ACTIONS = null;

/* ── what this tool's telemetry is for ────────────────────────────────────
 * Every event below answers ONE question that would otherwise be answered by
 * taste. Nothing is logged "just in case", and no birth date leaves the page:
 * the payloads carry SHAPES — was it a cusp, was a time given, was the eastern
 * year shifted — never a day, a month or a year.
 *
 *   stage ready → typing → answered → explored   where people stop
 *   split                                        is the measured 2.46 % real
 *   cusp_open → cusp_resolved                    does the refine panel work —
 *                                                the whole design rests on it
 *   section_open                                 which of the five sections
 *                                                earns the weight it costs
 *   tz_set                                       how often the device default
 *                                                is the wrong birthplace
 *   shifted                                      how many people learn their
 *                                                eastern animal is not the one
 *                                                a year-only table gave them
 *   live + tables_unavailable                    is 1900–2060 the right range,
 *                                                and did the table simply fail
 *   n                                            do people check more than one
 *                                                person in one sitting
 */
const SEEN_DATES = new Set();
let stageTyping = false, stageExplored = false, stageAnswered = false, tzTouched = false;
let cuspOpenAt = null;
let STATE = { y: null, m: null, d: null, time: null, tz: null, ayanamsa: 'lahiri' };

/* ── formatting ───────────────────────────────────────────────────────────── */

const signName = (i) => t('sign_' + SIGN_KEYS[i], SIGN_KEYS[i]);
/* The Unicode sign characters render as EMOJI on most platforms (coloured
   tiles, one style per vendor), which put the heading at odds with the ring
   right next to it. The shared glyph set draws the same marks as paths and
   looks the same everywhere, print and PDF included. */
const signSymbol = (i) => glyphSvg(SIGN_KEYS[i], 24, 'currentColor', 7);

/** Degrees as the tradition writes them: 29°44′ inside the sign. */
function fmtDeg(deg) {
    let d = Math.floor(deg);
    let m = Math.round((deg - d) * 60);
    if (m === 60) { m = 0; d += 1; }
    return d + '°' + String(m).padStart(2, '0') + '′';
}

/** Wall-clock fields of a UTC instant in `tz`, ROUNDED to the minute.
 *
 * Intl truncates, and truncation is wrong for this page specifically: an
 * ingress at 13:37:52 would print as 13:37, and the sentence next to it says
 * "you were born before the Sun changed sign" to somebody born at 13:37:30 —
 * true, and impossible to believe. Rounding puts the printed minute on the
 * far side of the real instant, so the reader can never find a birth time that
 * contradicts the printed one. Classification still uses full seconds. */
function wallParts(tz, date, exact) {
    if (!exact) date = new Date(date.getTime() + 30000);
    return wallPartsRaw(tz, date);
}

function wallPartsRaw(tz, date) {
    const f = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
    const g = {};
    for (const p of f.formatToParts(date)) if (p.type !== 'literal') g[p.type] = +p.value;
    return g;
}

function fmtDateTime(tz, date, exact) {
    const p = wallParts(tz, date, exact);
    return t('date_time_fmt', '{d} {month} {y}, {h}:{mi}')
        .replace('{d}', p.day)
        .replace('{month}', t('month_gen_' + p.month, ''))
        .replace('{y}', p.year)
        .replace('{h}', String(p.hour).padStart(2, '0'))
        .replace('{mi}', String(p.minute).padStart(2, '0'));
}
/** ":52" — the seconds the rounded display hides, shown where the tool shows
 *  its working. Exact, unrounded. */
function fmtSeconds(tz, date) {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', second: '2-digit' });
    const p = f.formatToParts(date).find((x) => x.type === 'second');
    return p ? ':' + p.value : '';
}

function fmtDate(tz, date) {
    const p = wallParts(tz, date);
    return t('date_fmt', '{d} {month} {y}')
        .replace('{d}', p.day).replace('{month}', t('month_gen_' + p.month, '')).replace('{y}', p.year);
}

/* ── timezone ─────────────────────────────────────────────────────────────
 * No city widget here. The 30 KB picker is the right primitive when a tool
 * needs coordinates; this one needs an OFFSET, and showing the offset with its
 * explanation is both lighter and more checkable than showing a city and hiding
 * the arithmetic. The zone list comes from the browser itself.
 *
 * The alias list exists because the zone ids are useless to the people who most
 * need this field: someone born in Свердловск or Куйбышев will not guess
 * "Asia/Yekaterinburg" or "Europe/Samara", and those are exactly the births
 * where Soviet decree time moves the answer.
 */
function zoneList() {
    let ids = [];
    try {
        if (typeof Intl.supportedValuesOf === 'function') ids = Intl.supportedValuesOf('timeZone');
    } catch (e) { /* falls through to the short list below */ }
    if (!ids.length) ids = (t('tz_fallback_list', '') || '').split(',').filter(Boolean);
    return ids;
}
function zoneLabel(id) {
    const city = id.split('/').pop().replace(/_/g, ' ');
    const named = t('tz_city_' + id, '');
    return (named || city) + ' · ' + id;
}
function guessZone() {
    try {
        const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (z && zoneIsKnown(z)) return z;
    } catch (e) { /* keep the default */ }
    return 'UTC';
}

/* ── the two date tables everyone else answers from ───────────────────────
 * Shown only to make the disagreement checkable. No site is named: the point is
 * that a fixed table cannot be right, not that some particular page is bad.
 */
const TABLE_A = [[3, 21], [4, 21], [5, 22], [6, 22], [7, 23], [8, 24], [9, 24], [10, 24], [11, 23], [12, 22], [1, 21], [2, 20]];
const TABLE_B = [[3, 21], [4, 20], [5, 21], [6, 21], [7, 23], [8, 23], [9, 23], [10, 23], [11, 22], [12, 22], [1, 20], [2, 19]];
function tableSign(table, month, day) {
    for (let i = 0; i < 12; i++) {
        const [m0, d0] = table[i], [m1, d1] = table[(i + 1) % 12];
        const after = month > m0 || (month === m0 && day >= d0);
        const before = month < m1 || (month === m1 && day < d1);
        if (m0 < m1 ? (after && before) : (after || before)) return i;
    }
    return -1;
}

/* ── resolving one birth moment ───────────────────────────────────────────── */

/* localToUtc() does not return an instant — it returns a VERDICT, because a
 * wall-clock time in a zone is not always one instant. On the spring-forward
 * night it is none ('gap': that clock reading never existed) and on the
 * autumn night it is two ('ambiguous': the hour ran twice). Taking .instants[0]
 * and moving on would be the usual way to lose that, and it matters here for
 * the same 2.5 % the whole tool is built around: an hour is enough to move the
 * answer across a sign boundary. */
function atLocal(tz, y, m, d, h, mi) {
    const v = localToUtc(tz, y, m, d, h, mi);
    if (v.kind === 'gap') {
        return { instant: new Date(v.gapSuggestion), kind: 'gap', alt: null };
    }
    return {
        instant: new Date(v.instants[0]),
        kind: v.kind,
        alt: v.kind === 'ambiguous' ? new Date(v.instants[1]) : null,
    };
}

/** Midpoint of the local calendar day — the honest stand-in for an unknown time. */
function noonUtc(tz, y, m, d) { return atLocal(tz, y, m, d, 12, 0).instant; }

async function resolve(instant) {
    if (TABLES) {
        const r = resolveFromTables(TABLES, instant);
        if (r) return r;
    } else if (!tableRetried) {
        /* One dropped request for a 6 KB file must not condemn the whole visit
           to the 106 KB path. The fallback is correct, so the failure is silent
           by construction — the only symptom is that every answer suddenly
           takes a second — which is exactly why it needs a retry rather than
           trust. Measured on a flaky dev origin: the first answer after a reset
           went from ~1.3 s to ~40 ms. */
        tableRetried = true;
        try { TABLES = await loadTables(window.__ZD_TABLES); } catch (e) { /* engine it is */ }
        if (TABLES) {
            const r = resolveFromTables(TABLES, instant);
            if (r) return r;
        }
    }
    /* Outside the table. Same answers, computed live — the engine is worth its
       106 KB to the handful of visitors who type a 17th-century date, and worth
       nothing to everyone else. */
    return resolveWithEngine(instant);
}

/** The sign at an instant, and nothing else — no ingress search. */
async function signOnly(instant, near) {
    /* Inside the already-resolved window the answer is known without any work
       at all. This is the common case: a day that does not straddle an ingress. */
    if (near && near.ingressStart && near.ingressEnd
        && instant >= near.ingressStart && instant < near.ingressEnd) return near.sign;
    if (TABLES) {
        const w = signWindow(TABLES, instant);
        if (w) return w.sign;
    }
    const A = await loadEngine();
    const lon = ((A.Ecliptic(A.GeoVector(A.Body.Sun, instant, true)).elon % 360) + 360) % 360;
    return Math.floor(lon / 30) % 12;
}

/**
 * Everything the page shows, for one birth date.
 *
 * `time` is null when unknown; the day is then judged as a whole and the answer
 * is reported as uncertain IF the Sun changed sign inside it.
 */
async function answerFor(y, m, d, time, tz) {
    const dayStart = atLocal(tz, y, m, d, 0, 0).instant;
    const nextLocal = new Date(Date.UTC(y, m - 1, d + 1));
    const dayEnd = atLocal(tz, nextLocal.getUTCFullYear(), nextLocal.getUTCMonth() + 1, nextLocal.getUTCDate(), 0, 0).instant;
    const local = time ? atLocal(tz, y, m, d, time.h, time.mi) : { instant: noonUtc(tz, y, m, d), kind: 'unique', alt: null };
    const instant = local.instant;

    const at = await resolve(instant);
    /* The two day boundaries only need a SIGN, and asking resolve() for them
       costs two more ingress searches each. In the table that is free; on the
       engine path it was most of the 5.9 s an out-of-range date took to first
       answer (measured 2026-09-16, cold load, dev origin). Now 3 searches
       instead of 12, and the boundaries reuse the answer's own window whenever
       they fall inside it — which, on 97.5 % of days, they both do. */
    const startSign = await signOnly(dayStart, at);
    const endSign = await signOnly(new Date(dayEnd.getTime() - 60000), at);

    /* The day is SPLIT when its first and last minute fall in different signs.
       That, not a date table, is what "born on the cusp" means. */
    const split = startSign !== endSign;
    const crossing = split
        ? (at.ingressStart > dayStart ? at.ingressStart : at.ingressEnd)
        : null;

    /* Even an unsplit day is worth a note when the crossing is within a day:
       the visitor may have the wrong time zone, and an hour of zone error is
       enough to move the answer there. */
    const gapToIngress = Math.min(
        Math.abs(instant - at.ingressStart),
        at.ingressEnd ? Math.abs(at.ingressEnd - instant) : Infinity
    );

    const sidValue = ayanamsaAt(STATE.ayanamsa, instant);
    const sid = siderealFrom(at.lon, sidValue);
    const dec = decanOf(at.sign, at.degInSign);

    return {
        y, m, d, time, tz, instant,
        sign: at.sign, lon: at.lon, degInSign: at.degInSign,
        ingressStart: at.ingressStart, ingressEnd: at.ingressEnd,
        live: !!at.live,
        split, crossing, dayStart, dayEnd,
        signBefore: startSign, signAfter: endSign,
        nearCusp: gapToIngress < DAY,
        gapToIngress,
        sidereal: { ...sid, value: sidValue, name: STATE.ayanamsa },
        decan: dec,
        constellation: at.constellation,
        eastern: at.eastern || (TABLES ? easternYear(TABLES, instant) : null),
        clock: local.kind,
        clockAlt: local.alt,
        offsetMs: zoneOffsetAt(tz, instant.getTime()),
        dstMs: dstShiftAt(tz, instant.getTime()),
        tableA: tableSign(TABLE_A, m, d),
        tableB: tableSign(TABLE_B, m, d),
    };
}

/* ── rendering ────────────────────────────────────────────────────────────── */

function signLabels() {
    const o = {};
    for (const k of SIGN_KEYS) o[k] = t('sign_' + k, k);
    return o;
}

function renderHero(a) {
    const cert = a.split && !a.time;
    const nameNow = signName(a.sign);
    let verdict, verdictClass;
    if (cert) {
        verdict = t('verdict_split', '')
            .replace('{a}', signName(a.signBefore))
            .replace('{b}', signName(a.signAfter))
            .replace('{time}', fmtDateTime(a.tz, a.crossing));
        verdictClass = 'is-warn';
    } else if (a.split && a.time) {
        /* Naming the side is the whole answer. "You are Aries, the others are
           Taurus" leaves the reader to work out which one they were, on the one
           page where that is the question. */
        const bornBefore = a.instant < a.crossing;
        verdict = t(bornBefore ? 'verdict_resolved_before' : 'verdict_resolved_after', '')
            .replace('{sign}', nameNow)
            .replace('{time}', fmtDateTime(a.tz, a.crossing))
            .replace('{other}', signName(bornBefore ? a.signAfter : a.signBefore));
        verdictClass = 'is-ok';
    } else if (a.nearCusp) {
        verdict = t('verdict_near', '')
            .replace('{sign}', nameNow)
            .replace('{hours}', Math.max(1, Math.round(a.gapToIngress / HOUR)));
        verdictClass = 'is-ok';
    } else {
        /* Instants, not dates. "Cancer from 21 June to 23 July" is wrong twice
           over: on 23 July the Sun is in Leo after 06:22, and on 21 June it was
           in Gemini until 06:33. Both endpoints are split days — this tool
           exists because of split days, so it cannot print them as whole ones. */
        verdict = t('verdict_plain', '')
            .replace('{sign}', nameNow)
            .replace('{from}', fmtDateTime(a.tz, a.ingressStart))
            .replace('{to}', a.ingressEnd ? fmtDateTime(a.tz, a.ingressEnd) : '');
        verdictClass = 'is-ok';
    }

    const heroSign = cert
        ? signName(a.signBefore) + ' ' + t('or', '/') + ' ' + signName(a.signAfter)
        : nameNow;

    /* `data-result-cue` is the declared contract of window.__resultCue
       (timbrica-core.js): on a touch screen, while this element is off-screen,
       it floats a pill that scrolls to it ON TAP. It matters here because the
       answer renders BELOW a form that is itself below a screenful of prose:
       measured on iPhone 14, the moment the date is complete the verdict sits
       934 px past the bottom edge, and nothing on screen says anything
       happened. Auto-scroll is forbidden site-wide; this is the allowed form. */
    return '<div class="zd-hero" data-result-cue aria-label="' + esc(t('cue_see_answer', '')) + '">'
        + '<div class="zd-hero-ring">' + ringSvg(a.lon, a.sign, signLabels(), { alt: heroSign }) + '</div>'
        + '<div class="zd-hero-text">'
        + '<p class="zd-hero-kicker">' + esc(t('hero_kicker', '')) + '</p>'
        + '<p class="zd-hero-sign"><span class="zd-hero-sym" aria-hidden="true">'
        + (cert ? signSymbol(a.signBefore) + '<span class="zd-hero-slash">/</span>' + signSymbol(a.signAfter) : signSymbol(a.sign))
        + '</span>' + esc(heroSign) + '</p>'
        + (cert ? '' : '<p class="zd-hero-deg">' + esc(t('hero_deg', '')
            .replace('{deg}', fmtDeg(a.degInSign)).replace('{sign}', signName(a.sign))) + '</p>')
        + '<p class="zd-verdict ' + verdictClass + '">' + verdict + '</p>'
        + '</div></div>';
}

function renderCusp(a) {
    if (!a.split) return '';
    const dayMs = a.dayEnd - a.dayStart;
    const frac = (a.crossing - a.dayStart) / dayMs;
    const birthFrac = a.time ? (a.instant - a.dayStart) / dayMs : null;
    const p = wallParts(a.tz, a.crossing);
    const hhmm = String(p.hour).padStart(2, '0') + ':' + String(p.minute).padStart(2, '0');

    return '<section class="zd-panel zd-cusp">'
        + '<h3>' + esc(t('cusp_title', '')) + '</h3>'
        + '<p class="zd-cusp-lead">' + t('cusp_lead', '')
            .replace('{time}', '<b>' + esc(hhmm) + '</b>')
            .replace('{date}', esc(fmtDate(a.tz, a.crossing)))
            .replace('{a}', '<b>' + esc(signName(a.signBefore)) + '</b>')
            .replace('{b}', '<b>' + esc(signName(a.signAfter)) + '</b>') + '</p>'
        + '<div class="zd-daybar-wrap">'
        + '<div class="zd-db-legend"><span class="zd-db-chip zd-el-' + elementOf(a.signBefore) + '">'
        + esc(signName(a.signBefore)) + '</span>'
        + '<span class="zd-db-at">' + esc(hhmm) + '</span>'
        + '<span class="zd-db-chip zd-el-' + elementOf(a.signAfter) + '">'
        + esc(signName(a.signAfter)) + '</span></div>'
        + dayBarHtml(frac, birthFrac, a.signBefore, a.signAfter, signLabels(), hhmm)
        + '</div>'
        + oddsLine(a)
        + '<label class="zd-slider-row"><span>' + esc(t('cusp_try', '')) + '</span>'
        + '<input type="range" id="zd-hourslider" min="0" max="1439" step="5"'
        + ' value="' + (a.time ? a.time.h * 60 + a.time.mi : 720) + '"'
        + ' aria-label="' + esc(t('cusp_try', '')) + '">'
        + '<output id="zd-hourout">' + (a.time ? String(a.time.h).padStart(2, '0') + ':' + String(a.time.mi).padStart(2, '0') : '12:00') + '</output>'
        + '</label>'
        + '<p class="zd-note">' + esc(t('cusp_tz_note', '')) + '</p>'
        + '</section>';
}

/* Shown only when the answer is split AND no time was given — the moment the
 * tool would otherwise have nothing left to say. Once an hour is named (typed
 * or dragged on the slider) the split resolves and this line goes away. */
function oddsLine(a) {
    if (!a.split || a.time) return '';
    const totalH = Math.round((a.dayEnd - a.dayStart) / 3600000);   // 23, 24 or 25 on a DST day
    let beforeH = Math.round((a.crossing - a.dayStart) / 3600000);
    beforeH = Math.max(0, Math.min(totalH, beforeH));
    /* The second half is DERIVED, never rounded on its own: two independent
       roundings print hours that do not add up to the day they describe. */
    const afterH = totalH - beforeH;
    const hrs = (n) => (n < 1 ? t('odds_under_hour', '') : t('odds_hours', '{n} h').replace('{n}', String(n)));
    return '<p class="zd-odds">' + esc(t('odds_line', '')
        .replace('{a}', signName(a.signBefore)).replace('{ha}', hrs(beforeH))
        .replace('{b}', signName(a.signAfter)).replace('{hb}', hrs(afterH))) + '</p>';
}

function factRow(label, value, hint) {
    return '<div class="zd-fact"><dt>' + esc(label) + '</dt><dd>' + value
        + (hint ? '<span class="zd-fact-hint">' + esc(hint) + '</span>' : '') + '</dd></div>';
}

function renderFacts(a) {
    const s = a.sign;
    const trad = RULERS_TRADITIONAL[s], mod = RULERS_MODERN[s];
    const rulers = trad === mod
        ? t('planet_' + trad, trad)
        : t('ruler_both', '{t} / {m}')
            .replace('{t}', t('planet_' + trad, trad))
            .replace('{m}', t('planet_' + mod, mod));
    const dec = a.decan;
    return '<section class="zd-panel"><h3>' + esc(t('facts_title', '')) + '</h3>'
        + '<dl class="zd-facts">'
        + factRow(t('f_element', ''), esc(t('element_' + elementOf(s), '')), t('element_hint_' + elementOf(s), ''))
        + factRow(t('f_modality', ''), esc(t('modality_' + modalityOf(s), '')), t('modality_hint_' + modalityOf(s), ''))
        + factRow(t('f_polarity', ''), esc(t('polarity_' + polarityOf(s), '')))
        + factRow(t('f_ruler', ''), esc(rulers), trad === mod ? '' : t('ruler_hint', ''))
        + factRow(t('f_decan', ''), esc(t('decan_n', '{n}').replace('{n}', dec.index + 1)
            + ' · ' + signName(dec.sign)), t('decan_hint', ''))
        + factRow(t('f_opposite', ''), esc(signName(oppositeOf(s))))
        + '</dl>'
        + '<p class="zd-tradition">' + esc(t('tradition_note', '')) + '</p>'
        + '</section>';
}

function renderProof(a) {
    const rows = [];
    rows.push([t('p_lon', ''), fmtDeg(a.degInSign) + ' ' + signName(a.sign)
        + ' <span class="zd-dim">(' + a.lon.toFixed(4) + '°)</span>']);
    /* EXACT here, not the rounded minute the rest of the page prints. Pasting
       the seconds onto a rounded minute invents a moment that never happened:
       the December 2000 solstice is 13:37:52 and would have been printed
       "13:38:52" — in the one block whose whole job is to show the working. */
    rows.push([t('p_in', ''), fmtDateTime(a.tz, a.ingressStart, true)
        + '<span class="zd-dim">' + fmtSeconds(a.tz, a.ingressStart) + '</span>']);
    if (a.ingressEnd) rows.push([t('p_out', ''), fmtDateTime(a.tz, a.ingressEnd, true)
        + '<span class="zd-dim">' + fmtSeconds(a.tz, a.ingressEnd) + '</span>']);
    rows.push([t('p_zone', ''), esc(a.tz) + ' · UTC' + formatOffset(a.offsetMs)
        + (a.dstMs ? ' <span class="zd-dim">(' + esc(t('p_dst', '')) + ')</span>' : '')]);
    rows.push([t('p_source', ''), esc(a.live ? t('p_source_live', '') : t('p_source_table', ''))]);

    let tableBlock = '';
    const aWrong = a.tableA !== a.sign, bWrong = a.tableB !== a.sign;
    if (!a.split || a.time) {
        if (aWrong || bWrong) {
            tableBlock = '<p class="zd-tblwarn">' + t('tbl_wrong', '')
                .replace('{sign}', '<b>' + esc(signName(a.sign)) + '</b>')
                .replace('{a}', esc(signName(a.tableA)))
                .replace('{b}', esc(signName(a.tableB))) + '</p>';
        } else {
            tableBlock = '<p class="zd-note">' + esc(t('tbl_ok', '')) + '</p>';
        }
    }

    return '<details class="zd-panel"><summary>' + esc(t('proof_title', '')) + '</summary>'
        + '<dl class="zd-proof">'
        + rows.map(([k, v]) => '<div><dt>' + esc(k) + '</dt><dd>' + v + '</dd></div>').join('')
        + '</dl>' + tableBlock
        + '<p class="zd-note">' + t('proof_note', '') + '</p>'
        + '</details>';
}

function renderSidereal(a) {
    const same = a.sidereal.sign === a.sign;
    return '<details class="zd-panel"><summary>' + esc(t('sid_title', '')) + '</summary>'
        + '<p class="zd-lead">' + (same
            ? t('sid_same', '').replace('{sign}', '<b>' + esc(signName(a.sign)) + '</b>')
            : t('sid_diff', '')
                .replace('{trop}', '<b>' + esc(signName(a.sign)) + '</b>')
                .replace('{sid}', '<b>' + esc(signName(a.sidereal.sign)) + '</b>')) + '</p>'
        + '<p>' + esc(t('sid_why', '')) + '</p>'
        + '<dl class="zd-proof"><div><dt>' + esc(t('sid_deg', '')) + '</dt><dd>'
        + esc(fmtDeg(a.sidereal.degInSign) + ' ' + signName(a.sidereal.sign)) + '</dd></div>'
        + '<div><dt>' + esc(t('sid_ayan', '')) + '</dt><dd>'
        + esc((AYANAMSHAS[a.sidereal.name] || {}).label + ' ' + a.sidereal.value.toFixed(3) + '°')
        + '</dd></div></dl>'
        + '<label class="zd-inline-field"><span>' + esc(t('sid_pick', '')) + '</span>'
        + '<select id="zd-ayanamsa" class="zd-select">'
        + Object.keys(AYANAMSHAS).map((k) => '<option value="' + k + '"'
            + (k === a.sidereal.name ? ' selected' : '') + '>' + esc(AYANAMSHAS[k].label) + '</option>').join('')
        + '</select></label>'
        + '</details>';
}

function renderConstellation(a) {
    if (!a.constellation || !a.constellation.key) return '';
    const c = a.constellation;
    const same = c.key === SIGN_KEYS[a.sign];
    const isOph = c.key === 'ophiuchus';
    return '<details class="zd-panel"><summary>' + esc(t('con_title', '')) + '</summary>'
        + '<p class="zd-lead">' + t('con_lead', '')
            .replace('{con}', '<b>' + esc(t('constel_' + c.key, c.key)) + '</b>')
            .replace('{from}', esc(fmtDate(a.tz, c.start)))
            .replace('{to}', esc(fmtDate(a.tz, new Date(c.end.getTime() - 60000)))) + '</p>'
        + (isOph ? '<p class="zd-callout">' + esc(t('con_ophiuchus', '')) + '</p>' : '')
        + (same ? '' : '<p>' + t('con_diff', '')
            .replace('{sign}', '<b>' + esc(signName(a.sign)) + '</b>')
            .replace('{con}', '<b>' + esc(t('constel_' + c.key, c.key)) + '</b>') + '</p>')
        + '<p>' + esc(t('con_why', '')) + '</p>'
        + '<p class="zd-note">' + esc(t('con_nasa', '')) + '</p>'
        + '</details>';
}

/* The lunar new year is a DATE IN CHINA, not an instant to re-localise. It is
   stored as the civil day the year opens on there, so formatting it in the
   visitor's zone shifts it: 7 February 2008 came out as 6 February for anyone
   west of Shanghai — the tool contradicting the very date it says everyone else
   gets wrong. */
const CHINA_TZ = 'Asia/Shanghai';

function renderEastern(a) {
    const e = a.eastern;
    if (!e) return '';
    const animal = t('animal_' + e.animal, e.animal);
    const element = t('el_' + e.element, e.element);
    const pole = e.yang ? t('yang', '') : t('yin', '');
    return '<details class="zd-panel"' + (e.shifted ? ' open' : '') + '><summary>'
        + esc(t('east_title', '')) + '</summary>'
        /* Animal and element are shown as two labelled parts, never glued into
           one noun phrase. "Fire Tiger" is one string in English and needs
           gender agreement in Russian (Огненный Тигр / Огненная Змея), French
           and Spanish — sixty precomposed forms per locale to say what a comma
           says for free. */
        + '<p class="zd-east-hero"><span class="zd-east-sym" aria-hidden="true">'
        + '🐀🐂🐅🐇🐉🐍🐎🐐🐒🐓🐕🐖'.match(/./gu)[e.animalIndex] + '</span>'
        + '<b>' + esc(animal) + '</b> <span class="zd-dim">'
        + esc(t('east_meta', '').replace('{el}', element).replace('{pole}', pole)) + '</span></p>'
        + (e.shifted
            ? '<p class="zd-callout">' + t('east_shifted', '')
                .replace('{animal}', '<b>' + esc(animal) + '</b>')
                /* The new year the visitor did NOT reach, not the one that
                   opened their lunar year — "the new year only came on 18 Feb
                   2007, and you were born before it" is false for someone born
                   in February 2008 and reads as a bug, because it is one. */
                .replace('{cny}', esc(e.nextNewYear ? fmtDate(CHINA_TZ, e.nextNewYear) : ''))
                .replace('{gyear}', a.y)
                .replace('{cyear}', e.chineseYear) + '</p>'
            : '<p>' + t('east_normal', '')
                .replace('{cny}', esc(fmtDate(CHINA_TZ, e.newYear)))
                .replace('{cyear}', e.chineseYear) + '</p>')
        + '<p class="zd-note">' + esc(t('east_why', '')) + '</p>'
        + '</details>';
}

function renderYearTable(a) {
    if (!TABLES) return '';
    const rows = [];
    /* The twelve ingresses that bound the visitor's birth year, in calendar
       order — the table a date list is trying and failing to be. */
    for (let i = 0; i < 12; i++) {
        const probe = new Date(Date.UTC(a.y, i, 15, 12));
        const w = signWindow(TABLES, probe);
        if (!w) continue;
        rows.push(w);
    }
    const seen = new Set(), uniq = [];
    for (const w of rows) {
        const k = w.start.getTime();
        if (!seen.has(k)) { seen.add(k); uniq.push(w); }
    }
    uniq.sort((x, y2) => x.start - y2.start);
    return '<details class="zd-panel"><summary>'
        + esc(t('cal_title', '').replace('{year}', a.y)) + '</summary>'
        + '<table class="zd-cal"><thead><tr><th>' + esc(t('cal_sign', '')) + '</th>'
        + '<th>' + esc(t('cal_when', '')) + '</th></tr></thead><tbody>'
        + uniq.map((w) => '<tr' + (w.sign === a.sign ? ' class="is-on"' : '') + '>'
            + '<td><span class="zd-cal-sym" aria-hidden="true">' + signSymbol(w.sign) + '</span> '
            + esc(signName(w.sign)) + '</td>'
            + '<td>' + esc(fmtDateTime(a.tz, w.start)) + '</td></tr>').join('')
        + '</tbody></table>'
        + '<p class="zd-note">' + esc(t('cal_note', '').replace('{tz}', a.tz)) + '</p>'
        + '</details>';
}

/* A section that cannot be computed must SAY so.
 *
 * Outside the table (a 17th-century date) — and, more often, when tables.json
 * simply did not arrive — the eastern sign and the constellation disappeared
 * from the page with no word at all. Measured by cutting the request: the
 * answer still came in 1.6 s via the full engine, correct, and two whole
 * sections were silently gone. A reader cannot tell "this tool does not do
 * that" from "this failed just now", and only one of those is worth reloading
 * for. */
function renderGaps(a) {
    const missing = [];
    if (!a.constellation) missing.push(t('con_title', ''));
    if (!a.eastern) missing.push(t('east_title', ''));
    if (!missing.length) return '';
    /* Three states, not two. With no table at all the range is unknown, and the
       out-of-range sentence would print «покрывает годы с  по .» — a broken
       line that blames the date for a network failure. */
    const known = !!TABLES;
    const inRange = known && a.y >= TABLES.from && a.y <= TABLES.to;
    return '<p class="zd-note zd-gap">'
        + esc((!known || inRange ? t('gap_table_missing', '') : t('gap_out_of_range', ''))
            .replace('{what}', missing.join(', '))
            .replace('{from}', TABLES ? TABLES.from : '')
            .replace('{to}', TABLES ? TABLES.to : ''))
        + '</p>';
}

/* The question that follows «I am Aries» in the same breath is «and are we
   compatible». That tool exists, is free, and is six links down in the related
   block — which is where a reader looks last. The URL comes from the blade
   (route()), so a renamed or retired sibling breaks the page build instead of
   quietly shipping a 404. */
function renderNext(a) {
    const url = window.__ZD_SYNASTRY;
    if (!url) return '';
    return '<p class="zd-next"><a href="' + esc(url) + '">' + esc(t('next_synastry', '')) + '</a></p>';
}

function renderActions() {
    return '<div class="zd-actions">'
        + '<button type="button" class="zd-btn zd-btn-ghost" id="zd-copy">' + esc(t('a_copy', '')) + '</button>'
        + '<button type="button" class="zd-btn zd-btn-ghost" id="zd-png">' + esc(t('a_png', '')) + '</button>'
        + '<button type="button" class="zd-btn zd-btn-ghost" id="zd-pdf">' + esc(t('a_pdf', '')) + '</button>'
        + '</div>';
}

/* What the rendered answer actually depends on. Two answers with the same
   signature are the same picture, and redrawing it is not merely wasted work:
   it destroys the DOM the visitor is currently interacting with. */
function signature(a) {
    return [a.y, a.m, a.d, a.time ? a.time.h + ':' + a.time.mi : '-', a.tz,
        a.sign, a.lon.toFixed(6), a.split ? 1 : 0, a.sidereal.name].join('|');
}
let renderedSig = null;

function render(a) {
    const out = $('zd-result');
    /* A blur re-fires `change` on a field whose value did not change, and the
       answer comes back identical — but the old code rebuilt the whole result
       anyway. Two things broke because of it, and both look like the tool being
       broken rather than busy:
         · the click that CAUSED the blur was swallowed. Pressing a result
           button blurs the year field, the rebuild lands between mousedown and
           mouseup, and the browser never fires `click` because the element it
           started on no longer exists. Typing a year and pressing "Картинка"
           did nothing at all, once, every time.
         · every open section snapped shut.
       Redrawing only when the answer is genuinely different fixes both. */
    const sig = signature(a);
    if (renderedSig === sig && !out.hidden) return;
    renderedSig = sig;

    /* Which sections were open, so that nudging the hour slider does not
       collapse the reading the visitor had expanded. */
    const open = new Set();
    out.querySelectorAll('details').forEach((d, i) => { if (d.open) open.add(i); });

    out.innerHTML = '<div id="zd-card">'
        + renderHero(a)
        + renderCusp(a)
        + renderFacts(a)
        + '</div>'
        + renderProof(a)
        + renderSidereal(a)
        + renderConstellation(a)
        + renderEastern(a)
        + renderYearTable(a)
        + renderGaps(a)
        + renderNext(a)
        + renderActions();
    out.hidden = false;
    out.querySelectorAll('details').forEach((d, i) => { if (open.has(i)) d.open = true; });
    wireResult(a);
    /* The refinement panel opens ITSELF when the date needs it — this is the
       whole point of the tool, and leaving it to the visitor to discover a
       collapsed "advanced" section would give the 2.5 % the same wrong
       confidence everyone else gives them. */
    if (a.split && !a.time) openRefine(true);
}

/* ── wiring ───────────────────────────────────────────────────────────────── */

function wireResult(a) {
    const sl = $('zd-hourslider');
    if (sl) {
        const apply = (commit) => {
            const v = +sl.value;
            const h = Math.floor(v / 60), mi = v % 60;
            $('zd-hourout').textContent = String(h).padStart(2, '0') + ':' + String(mi).padStart(2, '0');
            if (commit) {
                setTimeFor(a.y, a.m, a.d, h, mi);
                recompute('slider');
            }
        };
        sl.addEventListener('input', () => apply(false));
        sl.addEventListener('change', () => apply(true));
    }
    /* Five sections were built on a hunch. This is the only way to learn which
       of them a reader ever opens, and a section nobody opens is weight the
       page carries for nothing. */
    const SECTION_KEYS = ['proof', 'sidereal', 'constellation', 'eastern', 'calendar'];
    $('zd-result').querySelectorAll('details').forEach((d, i) => {
        if (d.dataset.zdWatched) return;
        d.dataset.zdWatched = '1';
        d.addEventListener('toggle', () => {
            if (!d.open) return;
            if (!stageExplored) { stageExplored = true; window.__stage && window.__stage('explored'); }
            window.__aeFunnel && window.__aeFunnel('section_open', { s: SECTION_KEYS[i] || ('n' + i) });
        });
    });

    const ay = $('zd-ayanamsa');
    if (ay) ay.addEventListener('change', () => {
        STATE.ayanamsa = ay.value;
        window.__toolSettings && window.__toolSettings.save(SLUG, 'ayanamsa', ay.value);
        recompute('ayanamsa');
    });
    /* Delegated once on the stable container rather than on the buttons, which
       are replaced on every redraw. Belt to the signature check's braces. */
    const out = $('zd-result');
    ACTIONS = { copy: () => copyLink(a), png: () => exportPng(a), pdf: () => exportPdf(a) };
    if (!out.dataset.zdWired) {
        out.dataset.zdWired = '1';
        out.addEventListener('click', (e) => {
            if (!e.target.closest) return;
            const btn = e.target.closest('#zd-copy,#zd-png,#zd-pdf');
            if (!btn || !ACTIONS) return;
            const fn = ACTIONS[btn.id.replace('zd-', '')];
            if (fn) fn();
        });
    }
}

function openRefine(auto) {
    const det = $('zd-refine');
    if (!det || det.open) return;
    det.open = true;
    if (auto) {
        const hint = $('zd-refine-why');
        if (hint) hint.hidden = false;
        window.__aeFunnel && window.__aeFunnel('refine_auto_open', {});
    }
}

/* ── input ────────────────────────────────────────────────────────────────── */

/* Returns the birth moment, or a REASON there is none. "Not filled in yet" and
   "that date does not exist" are different states and the old code merged them
   into null: a visitor who had typed a day and a year but not yet picked a
   month was told «такой даты не существует» — accused of a mistake they had
   not made, while they were still filling the form. */
const INCOMPLETE = { incomplete: true };
const INVALID = { invalid: true };

function readForm() {
    const dRaw = $('zd-day').value.trim();
    const mRaw = $('zd-month').value.trim();
    const yRaw = $('zd-year').value.trim();
    if (!dRaw || !mRaw || !yRaw) return INCOMPLETE;

    const d = parseInt(dRaw, 10);
    const m = parseInt(mRaw, 10);
    const y = parseInt(yRaw, 10);
    if (!(d >= 1 && d <= 31) || !(m >= 1 && m <= 12) || !(y >= 1 && y <= 4000)) return INVALID;
    /* 31 April is not a date. Reject rather than roll over: a silent roll-over
       answers a question the visitor did not ask. */
    const probe = new Date(Date.UTC(y, m - 1, d));
    if (probe.getUTCDate() !== d || probe.getUTCMonth() !== m - 1) return INVALID;

    /* An empty time field means "unknown", and the whole day is judged. There
       is no separate flag: a flag can disagree with the field, and on this page
       that disagreement decides the answer. */
    let time = null;
    const tv = $('zd-time').value;
    if (/^\d{1,2}:\d{2}$/.test(tv)) {
        const [h, mi] = tv.split(':').map(Number);
        if (h >= 0 && h < 24 && mi >= 0 && mi < 60) time = { h, mi };
    }
    const tz = $('zd-tz').value || STATE.tz || guessZone();
    return { y, m, d, time, tz };
}

/* ── the birth time belongs to ONE person ─────────────────────────────────
 * Measured on a real session (QA, 17.09.2026): check yourself on a cusp date,
 * drag the hour slider to 21:05, then type your husband's date — and 21:05 is
 * still in the field. The tool then answers for HIS date at HER hour, with the
 * same confidence as everything else it prints, and the refine panel stays open
 * showing a time nobody entered for him. On a cusp that is a wrong sign; off a
 * cusp it is a number attached to the wrong person.
 *
 * So the time is remembered together with the date it was given for, and a
 * different date drops it. Correcting a typo in the year costs re-entering the
 * time; the other direction costs a confidently wrong answer, and the two are
 * not the same size. */
let timeBelongsTo = null;                 // 'YYYY-MM-DD' the time was typed for
const dateKey = (y, m, d) => y + '-' + m + '-' + d;

/** Write a time INTO the form and record which date it belongs to.
 *  Every path that fills the time field must go through here: the hour slider
 *  set the field and then recompute() — which drops a time whose owner is
 *  unknown — wiped it in the same tick, so dragging the slider moved the label
 *  and undid the answer. Ownership is not bookkeeping, it is the thing that
 *  tells "this time is for THIS date" apart from "this time is left over". */
function setTimeFor(y, m, d, hh, mi) {
    $('zd-time').value = String(hh).padStart(2, '0') + ':' + String(mi).padStart(2, '0');
    timeBelongsTo = dateKey(y, m, d);
    const note = $('zd-time-reset');
    if (note) note.hidden = true;
}

function forgetTimeIfDateChanged() {
    const d = $('zd-day').value.trim(), m = $('zd-month').value.trim(), y = $('zd-year').value.trim();
    if (!d || !m || !y) return;
    const key = dateKey(y, m, d);
    if (timeBelongsTo === key) return;
    /* An ownerless time is not a leftover. Somebody who opens the panel, types
       an hour and only then fills the date means that hour for that date — the
       first version cleared it and looked like the page eating input. Only a
       time that demonstrably belonged to a DIFFERENT date is dropped. */
    if (timeBelongsTo === null) {
        if ($('zd-time').value) timeBelongsTo = key;
        return;
    }
    if ($('zd-time').value) {
        $('zd-time').value = '';
        const note = $('zd-time-reset');
        if (note) {
            note.hidden = false;
            clearTimeout(forgetTimeIfDateChanged._t);
            forgetTimeIfDateChanged._t = setTimeout(() => { note.hidden = true; }, 9000);
        }
        window.__aeFunnel && window.__aeFunnel('time_dropped_with_date', {});
    }
    timeBelongsTo = null;
}

let pending = 0;
async function recompute(why) {
    forgetTimeIfDateChanged();
    const f = readForm();
    const err = $('zd-error');
    if (f === INCOMPLETE) {
        /* Still filling it in. Silence, not an accusation. */
        err.hidden = true;
        $('zd-result').hidden = true;
        return;
    }
    if (f === INVALID) {
        err.hidden = false;
        err.textContent = t('err_date', '');
        $('zd-result').hidden = true;
        window.__aeFunnel && window.__aeFunnel('date_rejected', {});
        return;
    }
    err.hidden = true;
    if (!stageTyping) { stageTyping = true; window.__stage && window.__stage('typing'); }
    STATE = { ...STATE, ...f };
    const mine = ++pending;
    const startedAt = Date.now();
    /* Out-of-table dates load the full engine, which takes a second or so. The
       previous answer is still on screen and still looks authoritative, so it
       has to be marked as stale — otherwise the reader has no way to tell that
       the page is answering a question they already changed. */
    const out = $('zd-result');
    const busyTimer = setTimeout(() => {
        if (mine === pending) { out.setAttribute('aria-busy', 'true'); out.classList.add('is-stale'); }
    }, 250);
    try {
        const a = await answerFor(f.y, f.m, f.d, f.time, f.tz);
        if (mine !== pending) return;
        clearTimeout(busyTimer);
        out.removeAttribute('aria-busy');
        out.classList.remove('is-stale');
        if (f.time) timeBelongsTo = dateKey(f.y, f.m, f.d);
        /* Before render, not after: the strip is part of what render draws, and
           an entry added afterwards would first appear one answer late. */
        rememberChecked(a, f);
        render(a);
        reportAnswer(a, f, why, Date.now() - startedAt);
        updateUrl(a);
        if (window.__toolSettings) {
            window.__toolSettings.save(SLUG, 'last',
                f.y + '-' + String(f.m).padStart(2, '0') + '-' + String(f.d).padStart(2, '0'));
            /* The zone is remembered too. The default comes from the device, and
               the people who have to override it are exactly the ones born
               somewhere else — for them the default is wrong on every visit. */
            window.__toolSettings.save(SLUG, 'tz', f.tz);
        }
    } catch (e) {
        if (mine !== pending) return;
        clearTimeout(busyTimer);
        out.removeAttribute('aria-busy');
        out.classList.remove('is-stale');
        window.__handleError && window.__handleError(e, SLUG);
        err.hidden = false;
        err.textContent = t('err_generic', '');
    }
}

/** Accepts 20.04.1995 · 20/04/1995 · 20-4-1995 · 1995-04-20, and nothing else. */
export function parsePastedDate(text) {
    const m = String(text || '').trim().match(/^(\d{1,4})\s*[./\-]\s*(\d{1,2})\s*[./\-]\s*(\d{1,4})$/);
    if (!m) return null;
    let y, mo, d;
    if (m[1].length === 4) { y = +m[1]; mo = +m[2]; d = +m[3]; }      // ISO-ish
    else if (m[3].length === 4 || +m[3] > 31) { d = +m[1]; mo = +m[2]; y = +m[3]; }
    else return null;                                                  // 03/04/05 — unknowable
    if (!(mo >= 1 && mo <= 12) || !(d >= 1 && d <= 31) || !(y >= 1 && y <= 4000)) return null;
    const probe = new Date(Date.UTC(y, mo - 1, d));
    if (probe.getUTCDate() !== d || probe.getUTCMonth() !== mo - 1) return null;
    return { y, m: mo, d };
}

function onPasteDate(e) {
    const txt = (e.clipboardData || window.clipboardData || {}).getData
        ? (e.clipboardData || window.clipboardData).getData('text') : '';
    const v = parsePastedDate(txt);
    if (!v) return;                       // not a date — let the browser paste it
    e.preventDefault();
    $('zd-day').value = v.d;
    $('zd-month').value = String(v.m);
    $('zd-year').value = v.y;
    window.__aeFunnel && window.__aeFunnel('date_pasted', {});
    recompute('paste');
}

/* ── «я ещё мужа проверю» ─────────────────────────────────────────────────
 * The multi-person path is not a guess: it is the path that produced the worst
 * defect found in QA — person A's birth time silently applied to person B's
 * date. Once the tool has to defend against that, it may as well support it
 * properly, and a row of what you have already checked is the whole feature.
 *
 * Kept in memory for this page view only. A birth date is personal, the strip
 * is a convenience for one sitting, and nothing about it is worth writing to
 * the visitor's disk. */
const CHECKED = [];        // [{y,m,d,time,tz,sign,key}] newest last, max 6

/* Only a date the visitor SETTLED on gets a chip.
 *
 * The first cut recorded every answer, and every answer includes the ones made
 * of half-typed input: filling day, then month, then year produces three dates,
 * so two people left six chips — and tapping the newest one restored
 * «3 ноября 1987», a date nobody had entered (this person's day glued to the
 * previous person's month and year). A chip is a promise that you were here
 * before; a keystroke is not. */
let commitTimer = 0;

function rememberChecked(a, f) {
    clearTimeout(commitTimer);
    commitTimer = setTimeout(() => commitChecked(a, f), 1500);
}

function commitChecked(a, f) {
    const key = dateKey(f.y, f.m, f.d);
    const i = CHECKED.findIndex((c) => c.key === key);
    if (i >= 0) CHECKED.splice(i, 1);
    CHECKED.push({ key, y: f.y, m: f.m, d: f.d, time: f.time, tz: f.tz, sign: a.sign, split: a.split && !f.time });
    while (CHECKED.length > 6) CHECKED.shift();
    paintChecked(key);
}

/* The strip lives in its own container, NOT inside the answer: it changes on a
   timer of its own, and folding it into the answer's markup would mean
   repainting the whole verdict every time a chip is added. */
function paintChecked(currentKey) {
    const box = $('zd-checked');
    if (!box) return;
    box.innerHTML = renderChecked(currentKey);
    box.hidden = !box.innerHTML;
}

function renderChecked(cur) {
    if (CHECKED.length < 2) return '';
    const chips = CHECKED.slice().reverse().map((c) => {
        const label = fmtDate(c.tz, new Date(Date.UTC(c.y, c.m - 1, c.d, 12)));
        const name = c.split ? t('or', '/') : signName(c.sign);
        return '<button type="button" class="zd-chip' + (c.key === cur ? ' is-on' : '') + '"'
            + ' data-zd-recall="' + esc(c.key) + '">'
            + '<span class="zd-chip-sym" aria-hidden="true">' + signSymbol(c.sign) + '</span>'
            + esc(label) + (c.split ? '' : ' · ' + esc(name)) + '</button>';
    }).join('');
    return '<section class="zd-panel zd-checked"><h3>' + esc(t('checked_title', '')) + '</h3>'
        + '<div class="zd-chips">' + chips + '</div>'
        + '<p class="zd-note">' + esc(t('checked_note', '')) + '</p></section>';
}

function recallChecked(key) {
    const c = CHECKED.find((x) => x.key === key);
    if (!c) return;
    $('zd-day').value = c.d;
    $('zd-month').value = String(c.m);
    $('zd-year').value = c.y;
    if (c.tz) $('zd-tz').value = c.tz;
    if (c.time) setTimeFor(c.y, c.m, c.d, c.time.h, c.time.mi);
    else { $('zd-time').value = ''; timeBelongsTo = null; }
    window.__aeFunnel && window.__aeFunnel('checked_recall', {});
    paintChecked(key);
    recompute('recall');
}

/** One answer reached the screen — report its SHAPE, never its content. */
function reportAnswer(a, f, why, ms) {
    if (why === 'silent') return;
    const key = dateKey(f.y, f.m, f.d);
    if (!SEEN_DATES.has(key)) SEEN_DATES.add(key);

    /* A cusp date arriving WITHOUT a time is the moment the tool asks its one
       question; whether the visitor answers it is the number that says the
       design works. So the PAIR is reported, not just the end state. */
    if (a.split && !f.time) {
        if (cuspOpenAt !== key) {
            cuspOpenAt = key;
            window.__aeFunnel && window.__aeFunnel('cusp_open', {});
        }
    } else if (a.split && f.time && cuspOpenAt === key) {
        cuspOpenAt = null;
        window.__aeFunnel && window.__aeFunnel('cusp_resolved', { via: why === 'slider' ? 'slider' : 'field' });
    }

    /* In range but answered by the live engine ⇒ the table did not arrive.
       Outside the range that is the designed path, not a fault. */
    if (a.live && TABLES && f.y >= TABLES.from && f.y <= TABLES.to) {
        window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'tables_unavailable' });
    }

    if (!stageAnswered) { stageAnswered = true; window.__stage && window.__stage('answered'); }
    window.__aeToolComplete && window.__aeToolComplete({
        ok: true,
        split: a.split ? 1 : 0,
        timed: f.time ? 1 : 0,
        near: a.nearCusp ? 1 : 0,
        live: a.live ? 1 : 0,
        shifted: a.eastern && a.eastern.shifted ? 1 : 0,
        sid_diff: a.sidereal.sign !== a.sign ? 1 : 0,
        tz_set: tzTouched ? 1 : 0,
        n: SEEN_DATES.size,
        ms,
        why: why || 'input',
    });
}

/* ── permalink ────────────────────────────────────────────────────────────── */

function permalink(a) {
    const u = new URL(location.href);
    u.search = '';
    u.searchParams.set('d', a.y + '-' + String(a.m).padStart(2, '0') + '-' + String(a.d).padStart(2, '0'));
    if (a.time) {
        u.searchParams.set('t', String(a.time.h).padStart(2, '0') + ':' + String(a.time.mi).padStart(2, '0'));
        u.searchParams.set('tz', a.tz);
    }
    return u.toString();
}
function updateUrl(a) {
    try { history.replaceState(null, '', permalink(a)); } catch (e) { /* file:// and the like */ }
}
async function copyLink(a) {
    const link = permalink(a);
    try {
        await navigator.clipboard.writeText(link);
        window.__toast && window.__toast(t('a_copied', ''), 'success');
    } catch (e) {
        window.__toast && window.__toast(link, 'info');
    }
    window.__aeFunnel && window.__aeFunnel('link_copied', {});
}

/* ── export ───────────────────────────────────────────────────────────────── */

function fileBase(a) {
    return 'zodiac-' + a.y + '-' + String(a.m).padStart(2, '0') + '-' + String(a.d).padStart(2, '0');
}

/* The card is DRAWN, not screenshotted — see cardSvg() for why. */
function cardFor(a) {
    const cert = a.split && !a.time;
    return cardSvg({
        sign: a.sign, lon: a.lon,
        kicker: t('card_kicker', ''),
        title: cert
            ? signName(a.signBefore) + ' ' + t('or', '/') + ' ' + signName(a.signAfter)
            : signName(a.sign),
        degree: cert ? '' : fmtDeg(a.degInSign) + ' ' + signName(a.sign),
        line: cert ? t('card_line_split', '') : (t('card_line', '') || ''),
        date: fmtDate(a.tz, a.instant),
        brand: (window.__ZD_BRAND || 'Timbrica'),
    });
}

async function exportPng(a) {
    try {
        const blob = await svgToPngBlob(cardFor(a), 1);
        await window.__downloadFile(blob, fileBase(a) + '.png');
        window.__aeFunnel && window.__aeFunnel('export', { fmt: 'png' });
    } catch (e) {
        window.__toast && window.__toast(t('a_png_fail', ''), 'error');
        window.__aeFunnel && window.__aeFunnel('export_fail', { fmt: 'png' });
    }
}

/* The report script is deferred, so a click in the first moments can arrive
   before it. Loading it on demand beats the alternative, which is a button that
   does nothing and says nothing. */
function ensureReportPdf() {
    if (window.__generatePDF) return Promise.resolve(true);
    return new Promise((res) => {
        const s = document.createElement('script');
        /* Through __jsSrc, never a bare path: nginx serves /js/… `immutable` for
           180 days, so a visitor who already holds the file would never receive
           a fix to it — and it looks fine from here, because a fresh load always
           gets the new one (RuntimeScriptVersioningTest). */
        s.src = (window.__jsSrc ? window.__jsSrc('/js/report-pdf.js') : '/js/report-pdf.js');
        s.onload = () => res(!!window.__generatePDF);
        s.onerror = () => res(false);
        document.head.appendChild(s);
    });
}

async function exportPdf(a) {
    if (!(await ensureReportPdf())) {
        window.__toast && window.__toast(t('a_pdf_fail', ''), 'error');
        window.__aeFunnel && window.__aeFunnel('export_fail', { fmt: 'pdf', why: 'lib' });
        return;
    }
    window.__generatePDF({
        element: $('zd-result'),
        filename: fileBase(a) + '.pdf',
        title: signName(a.sign) + ' · ' + fmtDate(a.tz, a.instant),
    });
}

/* ── "the Sun is here right now" ──────────────────────────────────────────
 * A live line under the form. It demonstrates the tool before the visitor has
 * typed anything, and it answers a query of its own — "what sign is it now" —
 * which otherwise lands on a page that cannot answer without a birth date.
 */
async function renderNow() {
    const el = $('zd-now');
    if (!el) return;
    try {
        const now = new Date();
        const a = await resolve(now);
        el.innerHTML = t('now_line', '')
            .replace('{sign}', '<b>' + esc(signName(a.sign)) + '</b>')
            .replace('{sym}', '<span aria-hidden="true">' + signSymbol(a.sign) + '</span>')
            .replace('{deg}', esc(fmtDeg(a.degInSign)))
            .replace('{until}', a.ingressEnd ? esc(fmtDate(STATE.tz || guessZone(), a.ingressEnd)) : '');
        el.hidden = false;
    } catch (e) { /* decoration only */ }
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

/* A flat alphabetical list of 418 IANA ids is technically complete and
   practically unusable: the zones this locale's readers were actually born in
   are scattered through it, and a <select> only type-matches the visible label.
   So the detected zone comes first, then the locale's own shortlist, then
   everything else — the two cases that cover almost every visitor are at the
   top, and the long tail is still there for the rest. */
function fillZones(selected) {
    const sel = $('zd-tz');
    const near = (t('tz_fallback_list', '') || '').split(',').filter(Boolean);
    const all = zoneList();
    const seen = new Set();
    const add = (parent, id) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        const o = document.createElement('option');
        o.value = id;
        o.textContent = zoneLabel(id);
        if (id === selected) o.selected = true;
        parent.appendChild(o);
    };
    add(sel, selected);
    if (near.length) {
        const g = document.createElement('optgroup');
        g.label = t('tz_group_near', '');
        for (const id of near) add(g, id);
        if (g.childElementCount) sel.appendChild(g);
    }
    const rest = document.createElement('optgroup');
    rest.label = t('tz_group_all', '');
    for (const id of all) add(rest, id);
    if (rest.childElementCount) sel.appendChild(rest);
}

function prefill() {
    const q = new URLSearchParams(location.search);
    let iso = q.get('d');
    if (!iso && window.__toolSettings) iso = window.__toolSettings.load(SLUG, 'last', '') || null;
    if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const [y, m, d] = iso.split('-').map(Number);
        $('zd-year').value = y; $('zd-month').value = m; $('zd-day').value = d;
    }
    const tv = q.get('t');
    if (tv && /^\d{1,2}:\d{2}$/.test(tv)) {
        $('zd-time').value = tv;
        /* The link carried both, so the time belongs to the date beside it. */
        const dd = $('zd-day').value, mm = $('zd-month').value, yy = $('zd-year').value;
        if (dd && mm && yy) timeBelongsTo = dateKey(yy, mm, dd);
    }
    const saved = window.__toolSettings && window.__toolSettings.load(SLUG, 'ayanamsa', '');
    if (saved && AYANAMSHAS[saved]) STATE.ayanamsa = saved;
    return q.get('tz');
}

async function boot() {
    const urlTz = prefill();
    const savedTz = window.__toolSettings && window.__toolSettings.load(SLUG, 'tz', '');
    const tz = (urlTz && zoneIsKnown(urlTz)) ? urlTz
        : (savedTz && zoneIsKnown(savedTz)) ? savedTz
            : guessZone();
    STATE.tz = tz;
    fillZones(tz);

    /* People copy their birth date from somewhere and paste it. Before this,
       pasting «20.04.1995» into the day field left the field EMPTY (the browser
       refuses the non-numeric value for type=number) and nothing happened —
       the most natural shortcut produced the least feedback. */
    for (const id of ['zd-day', 'zd-month', 'zd-year']) {
        const el = $(id);
        if (el) el.addEventListener('paste', onPasteDate);
    }

    for (const id of ['zd-day', 'zd-month', 'zd-year', 'zd-time', 'zd-tz']) {
        const el = $(id);
        if (!el) continue;
        el.addEventListener('input', () => recompute('input'));
        el.addEventListener('change', () => recompute('input'));
    }
    const chips = $('zd-checked');
    if (chips) chips.addEventListener('click', (e) => {
        const c = e.target.closest && e.target.closest('[data-zd-recall]');
        if (c) recallChecked(c.getAttribute('data-zd-recall'));
    });
    $('zd-tz').addEventListener('change', () => {
        if (tzTouched) return;
        tzTouched = true;
        window.__aeFunnel && window.__aeFunnel('tz_changed', {});
    });
    $('zd-go').addEventListener('click', () => recompute('button'));
    $('zd-refine-toggle') && $('zd-refine-toggle').addEventListener('click', () => openRefine(false));

    /* The table is 6 KB and the page is useless without it, so it is fetched
       immediately rather than on first keystroke — but a failure is not fatal:
       resolve() falls through to the full engine. */
    try { TABLES = await loadTables(window.__ZD_TABLES); }
    catch (e) { window.__aeFunnel && window.__aeFunnel('tables_load_failed', {}); }

    renderNow();
    if ($('zd-day').value && $('zd-year').value) recompute('prefill');
    window.__stage && window.__stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
